import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSecureAuth } from '../../../hooks/useSecureApi';

import { User, Lock, Globe, CheckCircle } from 'react-feather';
import logo from '../../../assets/logo.png';
import GradientButton from '../common/GradientButton';

import {
  LoginPageContainer,
  DesktopPanel,
  PanelHeading,
  PanelDescription,
  DesktopLogoImage,
  TrustBadgesRow,
  TrustBadge,
  TrustBadgeIcon,
  TrustBadgeTitle,
  TrustBadgeDesc,
  RightPanel,
  FormCard,
  MobileHeader,
  MobileLogo,
  FormTitle,
  FormSubtitle,
  FormContainer,
  InputGroup,
  InputLabel,
  InputWrapper,
  InputIcon,
  StyledInput,
  ButtonWrapper,
  ButtonContent,
  FooterRow,
  FooterAction,
  LegalFooter,
  LegalText,
} from './LoginScreen.styles';
// --- VALIDATION SCHEMA ---
const loginSchema = yup.object({
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required'),
});

// --- COMPONENT ---
const LoginScreen = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState('');
  const { login, loading: isLoggingIn } = useSecureAuth();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm({
    resolver: yupResolver(loginSchema),
    mode: 'onChange',
    defaultValues: {
      username: '',
      password: ''
    }
  });

  const onSubmit = async (data) => {
    setLoginError('');

    const res = await login(data);
    if (res.success) {
      // Redirect based on user role
      if (res.user.role === 'cross_border') {
        navigate('/cross_border');
      } else {
        navigate('/business');
      }
    } else {
      setLoginError(res.error || 'Autenticación fallida');
    }
  };

  return (
    <LoginPageContainer>
      {/* ===== LEFT PANEL (desktop only) ===== */}
      <DesktopPanel>
        <DesktopLogoImage src={logo} alt="NYBANQ" />
        <PanelHeading>
          Banking{'\n'}Without{'\n'}Borders
        </PanelHeading>
        <PanelDescription>
          Multi-currency accounts, global transfers, and stablecoins — all in one secure platform.
        </PanelDescription>

        <TrustBadgesRow>
          <TrustBadge>
            <TrustBadgeIcon aria-hidden="true">
              <CheckCircle size={20} color="currentColor" aria-hidden="true" />
            </TrustBadgeIcon>
            <TrustBadgeTitle>Bank-Grade Security</TrustBadgeTitle>
            <TrustBadgeDesc>Enterprise-level compliance.</TrustBadgeDesc>
          </TrustBadge>
          <TrustBadge>
            <TrustBadgeIcon aria-hidden="true">
              <Globe size={20} color="currentColor" aria-hidden="true" />
            </TrustBadgeIcon>
            <TrustBadgeTitle>Multi-Currency</TrustBadgeTitle>
            <TrustBadgeDesc>USD, EUR.</TrustBadgeDesc>
          </TrustBadge>
        </TrustBadgesRow>

        <LegalText>
          <p>
            NYBANQ Payments USA Corp Business Deposit Account deposits held at Cross River Bank, Member FDIC, insured up to $250,000. NYBANQ Payments USA Corp is not an FDIC-insured bank. Deposit insurance covers the failure of an insured bank.
          </p>
          <p>
            NYBANQ Payments USA Corp FBO account deposits held at Cross River Bank, Member FDIC, insured up to $250,000. NYBANQ Payments USA Corp is not an FDIC-insured bank. Deposit insurance covers the failure of an insured bank. FDIC insurance is made available through pass-through insurance at Cross River Bank, where we have a direct relationship for the placement of deposits and into which customer funds are deposited, but only if certain conditions have been met.
          </p>
        </LegalText>
      </DesktopPanel>

      {/* ===== RIGHT PANEL ===== */}
      <RightPanel>
        <FormCard>
          {/* Mobile-only logo */}
          <MobileHeader>
            <MobileLogo src={logo} alt="NYBANQ Logo" />
          </MobileHeader>

          {/* Form Header */}
          <FormTitle>Welcome to NYBANQ</FormTitle>
          <FormSubtitle>Access your unified dashboard</FormSubtitle>

          {/* Form */}
          <FormContainer onSubmit={handleSubmit(onSubmit)} aria-label="Login form">
            <InputGroup>
              <InputLabel htmlFor="username-input">Username</InputLabel>
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <>
                    <InputWrapper style={{ borderColor: errors.username ? theme.colors.danger.main : undefined }}>
                      <InputIcon aria-hidden="true"><User size={18} aria-hidden="true" /></InputIcon>
                      <StyledInput
                        {...field}
                        id="username-input"
                        type="text"
                        placeholder="Enter your username"
                        aria-required="true"
                        aria-invalid={errors.username ? "true" : "false"}
                        aria-describedby={errors.username ? "username-error" : undefined}
                      />
                    </InputWrapper>
                    {errors.username && (
                      <span id="username-error" style={{ color: theme.colors.danger.main, fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        {errors.username.message}
                      </span>
                    )}
                  </>
                )}
              />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="password-input">Password</InputLabel>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <>
                    <InputWrapper style={{ borderColor: errors.password ? theme.colors.danger.main : undefined }}>
                      <InputIcon aria-hidden="true"><Lock size={18} aria-hidden="true" /></InputIcon>
                      <StyledInput
                        {...field}
                        id="password-input"
                        type="password"
                        placeholder="Enter your password"
                        aria-required="true"
                        aria-invalid={errors.password ? "true" : "false"}
                        aria-describedby={errors.password ? "password-error" : undefined}
                      />
                    </InputWrapper>
                    {errors.password && (
                      <span id="password-error" style={{ color: theme.colors.danger.main, fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        {errors.password.message}
                      </span>
                    )}
                  </>
                )}
              />
            </InputGroup>

            <ButtonWrapper>
              <GradientButton
                colors={['#333333', theme.colors.text.dark]}
                onPress={handleSubmit(onSubmit)}
                disabled={!isValid || isLoggingIn}
                style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '15px', borderRadius: '10px' }}
                aria-label="Secure Login"
              >
                <ButtonContent>
                  <Lock size={16} aria-hidden="true" />
                  {isLoggingIn ? 'Authenticating...' : 'Secure Login'}
                </ButtonContent>
              </GradientButton>
            </ButtonWrapper>
          </FormContainer>

          {loginError && (
            <div role="alert" style={{ color: theme.colors.danger.main, textAlign: 'center', fontSize: '14px', fontWeight: '500', marginTop: '16px', padding: '0 30px' }}>
              {loginError}
            </div>
          )}

          {/* Footer links */}
          <FooterRow>
            <FooterAction>Forgot Credentials?</FooterAction>
            <FooterAction onClick={() => navigate('/create-user')}>Create Account</FooterAction>
          </FooterRow>

        </FormCard>
        <LegalFooter>
          <span>© 2026 NYBANQ. All rights reserved.</span>
          <a href="/privacy.html">Privacy Policy</a>
          <a href="/terms.html">Terms & Conditions</a>
        </LegalFooter>
      </RightPanel>


    </LoginPageContainer>
  );
};

export default LoginScreen;